import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  UserBasicInfoResp, 
  UserCurrentTaskResp, 
  UserHistoryTaskResp, 
  UserHistoryByDateResp,
  UserGradeInfoResp,
  UserProjectGradeInfoResp
} from 'domain/payload/response/user/UserViewResp';
import InstallerService from 'service/InstallerService';
import ProjectService from 'service/ProjectService';
import { TaskViewType, HistoryViewType } from './types';
import LoadingOverlay from './components/LoadingOverlay';
import UserBasicInfoCard from './components/UserBasicInfoCard';
import UserDeviceInfoCard from './components/UserDeviceInfoCard';
import CurrentTasksCard from './components/CurrentTasksCard';
import TaskHistoryCard from './components/TaskHistoryCard';

const UserDetailModal: React.FC<{
  show: boolean;
  onClose: () => void;
  userId?: string;
  token?: string; 
}> = ({
  show,
  onClose,
  userId,
  token
}) => {
  const [userBasicInfo, setUserBasicInfo] = useState<UserBasicInfoResp | null>(null);
  const [userGradeInfo, setUserGradeInfo] = useState<UserGradeInfoResp | null>(null);
  const [userGradeProjects, setUserGradeProjects] = useState<UserProjectGradeInfoResp[]>([]);
  const [userTasks, setUserTasks] = useState<UserCurrentTaskResp[] | null>(null);
  const [userHistory, setUserHistory] = useState<UserHistoryTaskResp[] | null>(null);
  const [userHistoryByDate, setUserHistoryByDate] = useState<UserHistoryByDateResp[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyByDateLoading, setHistoryByDateLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  
  const [tasksSearch, setTasksSearch] = useState('');
  const [tasksCurrentPage, setTasksCurrentPage] = useState(1);
  const [tasksPageSize, setTasksPageSize] = useState(10);
  const [tasksTotalRecords, setTasksTotalRecords] = useState(0);
  const [tasksTotalPages, setTasksTotalPages] = useState(1);
  
  const [historySearch, setHistorySearch] = useState('');
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [historyTotalRecords, setHistoryTotalRecords] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyByDateCurrentPage, setHistoryByDateCurrentPage] = useState(1);
  const [historyByDatePageSize, setHistoryByDatePageSize] = useState(10);
  const [historyByDateTotalRecords, setHistoryByDateTotalRecords] = useState(0);
  const [historyByDateTotalPages, setHistoryByDateTotalPages] = useState(1);
  
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  
  const [taskViewType, setTaskViewType] = useState<TaskViewType>('list');
  const [historyViewType, setHistoryViewType] = useState<HistoryViewType>('all');
  
  const [selectedTask, setSelectedTask] = useState<UserCurrentTaskResp | null>(null);

  const [contentHeight] = useState('450px');
  const [allTasksForMap, setAllTasksForMap] = useState<UserCurrentTaskResp[]>([]);
  const [projectList, setProjectList] = useState<any[]>([]);
  const [selectedProjectForTasks, setSelectedProjectForTasks] = useState<string | null>(null);
  const [selectedProjectForHistory, setSelectedProjectForHistory] = useState<string | null>(null);

  const userService = useMemo(() => InstallerService(), []);
  const projectService = useMemo(() => ProjectService(), []);

  const handleWhatsAppClick = useCallback((phoneNumber: string) => {
    if (!phoneNumber) return;
    
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    let whatsappNumber = cleanNumber;
    
    if (whatsappNumber.startsWith('0')) {
      whatsappNumber = '62' + whatsappNumber.substring(1);
    }
    
    window.open(`https://wa.me/${whatsappNumber}`, '_blank');
  }, []);

  const fetchUserData = async () => {
    if (!userId || !token) return;
    
    setLoading(true);
    try {
      const projPromise = projectService.projectList(token, null, 1, 100, null).catch(() => null);
      const [basicInfoRes, tasksRes, historyRes, projRes] = await Promise.allSettled([
        userService.userView(token, userId),
        userService.userTask(token, userId, 1, 10, '', selectedProjectForTasks),
        userService.userHistory(token, userId, 1, 10, '', selectedProjectForHistory),
        projPromise
      ]);

      if (basicInfoRes.status === 'fulfilled') {
        const viewData: any = basicInfoRes.value?.data;
        if (viewData && 'basicInfo' in viewData) {
          setUserBasicInfo(viewData.basicInfo || null);
          setUserGradeInfo(viewData.grade || null);
          setUserGradeProjects(viewData.gradeProjects || []);
        } else {
          setUserBasicInfo(viewData || null);
          setUserGradeInfo(null);
          setUserGradeProjects([]);
        }
      }

      if (tasksRes.status === 'fulfilled') {
        const response = tasksRes.value;
        if (response?.pagination) {
          setTasksTotalRecords(response.pagination.totalRecords || 0);
          setTasksTotalPages(response.pagination.totalPage || 1);
        }
        setUserTasks(response?.data || []);
      }

      if (historyRes.status === 'fulfilled') {
        const response = historyRes.value;
        if (response?.pagination) {
          setHistoryTotalRecords(response.pagination.totalRecords || 0);
          setHistoryTotalPages(response.pagination.totalPage || 1);
        }
        setUserHistory(response?.data || []);
      }

      if (projRes && projRes.status === 'fulfilled' && projRes.value) {
        setProjectList(projRes.value.data || []);
      }

    } catch (err) {
      console.error("Error fetching user data:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadTasksData = async (page: number = tasksCurrentPage, size: number = tasksPageSize, search: string = tasksSearch, forMap: boolean = false, projectIdParam?: string | null) => {
    if (!token || !userId) return;
    if (forMap) {
      setMapLoading(true);
    } else {
      setTasksLoading(true);
    }
    
    try {
      const pageSizeForLoad = forMap ? 999 : size;
      const proj = projectIdParam !== undefined ? projectIdParam : selectedProjectForTasks;

      const tasksData = await userService.userTask(token, userId, page, pageSizeForLoad, search, proj);
      
      if (forMap) {
        setAllTasksForMap(tasksData?.data || []);
      } else {
        if (tasksData?.pagination) {
          setTasksTotalRecords(tasksData.pagination.totalRecords || 0);
          setTasksTotalPages(tasksData.pagination.totalPage || 1);
        }
        setUserTasks(tasksData?.data || []);
      }
      
      return tasksData?.data || [];
    } catch (error) {
      console.error('Error loading tasks:', error);
      return [];
    } finally {
      if (forMap) {
        setMapLoading(false);
      } else {
        setTasksLoading(false);
      }
    }
  };

  const loadHistoryData = async (projectIdParam?: string | null) => {
    if (!token || !userId) return;
    
    setHistoryLoading(true);
    try {
      const proj = projectIdParam !== undefined ? projectIdParam : selectedProjectForHistory;
      const historyData = await userService.userHistory(token, userId, historyCurrentPage, historyPageSize, historySearch, proj);

      if (historyData?.pagination) {
        setHistoryTotalRecords(historyData.pagination.totalRecords || 0);
        setHistoryTotalPages(historyData.pagination.totalPage || 1);
      }
      setUserHistory(historyData?.data || []);
      
      return historyData?.data || [];
    } catch (error) {
      console.error('Error loading history:', error);
      return [];
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadHistoryByDateData = async (forMap: boolean = false, projectIdParam?: string | null) => {
    if (!token || !userId || !selectedDate) return;

    setHistoryByDateLoading(true);
    try {
      const pageSizeForLoad = forMap ? 9999 : 9999;

      const proj = projectIdParam !== undefined ? projectIdParam : selectedProjectForHistory;

      const historyData = await userService.userHistoryByDate(
        token,
        userId,
        selectedDate,
        historyByDateCurrentPage,
        pageSizeForLoad,
        '',
        proj
      );

      if (historyData?.pagination) {
        setHistoryByDateTotalRecords(historyData.pagination.totalRecords || 0);
        setHistoryByDateTotalPages(historyData.pagination.totalPage || 1);
      }

      setUserHistoryByDate(historyData?.data || []);

      return historyData?.data || [];
    } catch (error) {
      console.error('Error loading history by date:', error);
      return [];
    } finally {
      setHistoryByDateLoading(false);
    }
  };

  const handleTasksSearchClick = (e: React.FormEvent) => {
    e.preventDefault();
    setTasksCurrentPage(1);
    if (taskViewType === 'map') {
      loadTasksData(1, 999, tasksSearch, true);
    } else {
      loadTasksData();
    }
  };

  const handleHistorySearchClick = (e: React.FormEvent) => {
    e.preventDefault();
    setHistoryCurrentPage(1);
    loadHistoryData();
  };

  const handleProjectChangeForTasks = (projectId: string | null) => {
    setSelectedProjectForTasks(projectId);
    setTasksCurrentPage(1);
    if (taskViewType === 'map') {
      loadTasksData(1, 999, tasksSearch, true, projectId);
    } else {
      loadTasksData(1, tasksPageSize, tasksSearch, false, projectId);
    }
  };

  const handleProjectChangeForHistory = (projectId: string | null) => {
    setSelectedProjectForHistory(projectId);
    setHistoryCurrentPage(1);
    loadHistoryData(projectId);
    if (historyViewType === 'daily-route') {
      loadHistoryByDateData(true, projectId).catch(() => {});
    }
  };

  const handleTasksPageSizeChange = (size: number) => {
    setTasksPageSize(size);
    setTasksCurrentPage(1);
    if (taskViewType === 'map') {
      loadTasksData(1, 999, tasksSearch, true);
    } else {
      loadTasksData();
    }
  };

  const handleHistoryPageSizeChange = (size: number) => {
    setHistoryPageSize(size);
    setHistoryCurrentPage(1);
    loadHistoryData();
  };

  const handleHistoryByDatePageSizeChange = (size: number) => {
    setHistoryByDatePageSize(size);
    setHistoryByDateCurrentPage(1);
    loadHistoryByDateData();
  };

  const handleTaskViewTypeChange = useCallback((type: TaskViewType) => {
    setTaskViewType(type);
    setSelectedTask(null);
    
    if (type === 'map') {
      if (allTasksForMap.length > 0) {
        if (allTasksForMap.length === 0) {
          setMapLoading(true);
          loadTasksData(1, 999, tasksSearch, true);
        }
      } else {
        setMapLoading(true);
        loadTasksData(1, 999, tasksSearch, true);
      }
    }
  }, [tasksSearch, userId, token, allTasksForMap]);

  const handleHistoryViewTypeChange = (type: HistoryViewType) => {
    setHistoryViewType(type);
    if (type === 'daily-route' && selectedDate) {
      loadHistoryByDateData(true);
    }
  };

  useEffect(() => {
    if (show && userId && token) {
      fetchUserData();
    }
  }, [show, userId, token]);

  useEffect(() => {
    setSelectedProjectForTasks(null);
    setSelectedProjectForHistory(null);
  }, [userId]);

  useEffect(() => {
    if (!show) {
      setUserBasicInfo(null);
      setUserGradeInfo(null);
      setUserGradeProjects([]);
      setUserTasks(null);
      setUserHistory(null);
      setUserHistoryByDate(null);
      setTasksCurrentPage(1);
      setHistoryCurrentPage(1);
      setHistoryByDateCurrentPage(1);
      setTasksSearch('');
      setHistorySearch('');
      setAllTasksForMap([]);
      setSelectedDate(new Date().toISOString().split('T')[0]);
      setHistoryViewType('all'); 
      setMapLoading(false);
      setTaskViewType('list');
    }
  }, [show]);

  useEffect(() => {
    if (userId && token && taskViewType === 'list') {
      loadTasksData();
    }
  }, [tasksCurrentPage, tasksPageSize, tasksSearch]);

  useEffect(() => {
    if (userId && token && historyViewType === 'all') {
      loadHistoryData();
    }
  }, [historyCurrentPage, historyPageSize, historySearch]);

  useEffect(() => {
    if (userId && token && historyViewType === 'daily-route' && selectedDate) {
      loadHistoryByDateData(true);
    }
  }, [selectedDate, historyViewType]);

  useEffect(() => {
    if (taskViewType !== 'map') {
      setMapLoading(false);
    }
  }, [taskViewType]);

  useEffect(() => {
    if (taskViewType === 'map' && allTasksForMap.length === 0 && userId && token && !mapLoading) {
      setMapLoading(true);
      loadTasksData(1, 999, tasksSearch, true);
    }
  }, [taskViewType, userId, token]);

  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content position-relative">
          <LoadingOverlay loading={loading} />
          
          <div className="modal-header">
            <h5 className="modal-title">User Detail</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
              aria-label="Close"
              disabled={loading || mapLoading}
            ></button>
          </div>
          
          <div className="modal-body">
            {userBasicInfo ? (
              <div className="row">
                <div className="col-md-6 mb-4">
                  <UserBasicInfoCard 
                    userInfo={userBasicInfo} 
                    gradeInfo={userGradeInfo}
                    onWhatsAppClick={handleWhatsAppClick} 
                  />
                </div>

                <div className="col-md-6 mb-4">
                  <UserDeviceInfoCard 
                    userInfo={userBasicInfo} 
                    gradeInfo={userGradeInfo}
                    gradeProjects={userGradeProjects}
                  />
                </div>

                <div className="col-md-6 mb-4">
                  <CurrentTasksCard
                    tasks={userTasks}
                    allTasksForMap={allTasksForMap}
                    loading={tasksLoading}
                    taskViewType={taskViewType}
                    contentHeight={contentHeight}
                    search={tasksSearch}
                    currentPage={tasksCurrentPage}
                    pageSize={tasksPageSize}
                    totalRecords={tasksTotalRecords}
                    totalPages={tasksTotalPages}
                    selectedTask={selectedTask}
                    userId={userId}
                    projectList={projectList}
                    selectedProjectId={selectedProjectForTasks}
                    onProjectChange={handleProjectChangeForTasks}
                    mapLoading={mapLoading}
                    onSearchChange={setTasksSearch}
                    onSearchSubmit={handleTasksSearchClick}
                    onTaskViewTypeChange={handleTaskViewTypeChange}
                    onPageChange={setTasksCurrentPage}
                    onPageSizeChange={handleTasksPageSizeChange}
                    onSelectTask={setSelectedTask}
                  />
                </div>

                <div className="col-md-6 mb-4">
                  <TaskHistoryCard
                    history={userHistory}
                    historyByDate={userHistoryByDate}
                    loading={historyLoading}
                    historyByDateLoading={historyByDateLoading}
                    historyViewType={historyViewType}
                    contentHeight={contentHeight}
                    search={historySearch}
                    currentPage={historyCurrentPage}
                    historyByDateCurrentPage={historyByDateCurrentPage}
                    pageSize={historyPageSize}
                    historyByDatePageSize={historyByDatePageSize}
                    totalRecords={historyTotalRecords}
                    historyByDateTotalRecords={historyByDateTotalRecords}
                    totalPages={historyTotalPages}
                    historyByDateTotalPages={historyByDateTotalPages}
                    selectedDate={selectedDate}
                    userId={userId}
                    projectList={projectList}
                    selectedProjectId={selectedProjectForHistory}
                    onProjectChange={handleProjectChangeForHistory}
                    onSearchChange={setHistorySearch}
                    onSearchSubmit={handleHistorySearchClick}
                    onHistoryViewTypeChange={handleHistoryViewTypeChange}
                    onPageChange={setHistoryCurrentPage}
                    onHistoryByDatePageChange={setHistoryByDateCurrentPage}
                    onPageSizeChange={handleHistoryPageSizeChange}
                    onHistoryByDatePageSizeChange={handleHistoryByDatePageSizeChange}
                    onDateChange={setSelectedDate}
                  />
                </div>
              </div>
            ) : !loading && (
              <div className="text-center py-4">
                <i className="fa-solid fa-exclamation-triangle fa-3x text-muted mb-3"></i>
                <p className="text-muted">No user data available</p>
              </div>
            )}
          </div>
          
          <div className="modal-footer border-top">
            <button 
              type="button" 
              className="btn btn-outline-secondary" 
              onClick={onClose}
              disabled={loading || mapLoading}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;